import {
  Box,
  Checkbox,
  Grid,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Criteria,
  GetInspectionItem,
} from '../../../../../../services/apiTypes';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  isNa,
  isEmpty,
  getIdIndex,
  getColorByJudgement,
  roundValue,
  formatNumberValue,
} from '../../../../../../_utils/inspectionUtils';
import { useDispatch } from 'react-redux';
import {
  updateJudgement,
  updateNumericalValue,
} from '../../../../../../stores/inspectionTypeData/slice';
import {
  JUDGEMENT_LABEL,
  DECIMAL_MAX_DIGITS,
  VARIABLE_HARDNESS,
  JUDGEMENT,
  INTEGER_MAX_DIGITS,
} from '../../../../../../_constants/inspectionConst';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined } from '../../../../../../_utils/utils';
import {
  HIDE_SPIN_BUTTON_STYLE,
  TEXT_OVERFLOW_2LINES,
} from '../../../../../../_constants/styleConst';
import VisibilityIcon from '@mui/icons-material/Visibility';

type VariableHardnessProps = {
  typeLabel: string;
  itemDataList: GetInspectionItem[];
  isVisualInspection: boolean;
};

/** 硬度可変測定 */
const VariableHardness: React.FC<VariableHardnessProps> = ({
  typeLabel,
  itemDataList,
  isVisualInspection,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const style = {
    columnLabel: {
      alignSelf: 'flex-end',
      fontSize: theme.typography.caption.fontSize,
    },
    numberInput: {
      borderRadius: '4px',
      bgcolor: 'background.default',
      ...HIDE_SPIN_BUTTON_STYLE,
    },
    unit: {
      marginLeft: '3px',
      alignSelf: 'flex-end',
      fontSize: theme.typography.caption.fontSize,
    },
  };
  /** 左側の値のデータ */
  const lowValueData = itemDataList[0];
  /** 右側の値のデータ */
  const highValueData = itemDataList[1];
  /** 差分のデータ */
  const diffValueData = itemDataList[2];
  /** 行のラベルリスト(左右で同じ前提) */
  const rowLabels = lowValueData.itemData.map((problem) => problem.label) ?? [];
  /** 平均のインデックス */
  const averageIndex = rowLabels.length - 1;

  const initLowValues = () => {
    return lowValueData.itemData.map((item) =>
      isNullOrUndefined(item.numericalValue)
        ? ''
        : item.numericalValue.toString(),
    );
  };

  const initHighValues = () => {
    return highValueData.itemData.map((item) =>
      isNullOrUndefined(item.numericalValue)
        ? ''
        : item.numericalValue.toString(),
    );
  };

  /** 各種表示用の数値文字列 */
  const [lowValues, setLowValues] = useState<string[]>(() => initLowValues());
  const [highValues, setHighValues] = useState<string[]>(() =>
    initHighValues(),
  );
  const [diffValue, setDiffValue] = useState<string>(
    isNullOrUndefined(diffValueData.numericalValue)
      ? ''
      : diffValueData.numericalValue.toString(),
  );

  useEffect(() => {
    // クリア押下時
    if (
      isEmpty(diffValueData.judgement) &&
      lowValueData.itemData.every((item) => item.numericalValue === null) &&
      highValueData.itemData.every((item) => item.numericalValue === null)
    ) {
      setLowValues(Array(lowValueData.itemData.length).fill(''));
      setHighValues(Array(highValueData.itemData.length).fill(''));
      setDiffValue('');
    }
    // 全てN/A押下時
    if (isNa(diffValueData.judgement)) {
      clearValues();
    }
  }, [diffValueData.judgement, lowValueData.itemData, highValueData.itemData]);

  const handleLowValueChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    setLowValues((prev) => {
      const newValues = [...prev];
      newValues[index] = formatNumberValue(
        event.target.value,
        INTEGER_MAX_DIGITS,
        DECIMAL_MAX_DIGITS,
      );
      return newValues;
    });
  };

  const handleHighValueChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    setHighValues((prev) => {
      const newValues = [...prev];
      newValues[index] = formatNumberValue(
        event.target.value,
        INTEGER_MAX_DIGITS,
        DECIMAL_MAX_DIGITS,
      );
      return newValues;
    });
  };

  /** 入力確定時に値を更新して判定 */
  const judgeInputValue = (isLow: boolean, index: number) => {
    let lowAverage = 0;
    let highAverage = 0;

    if (isLow) {
      const validatedValue = validateValue(lowValues[index]);
      setLowValues((prev) => {
        const newValues = [...prev];
        newValues[index] = validatedValue;
        return newValues;
      });
      const problemId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(lowValueData.id) + index + 1}`;
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [lowValueData.id, problemId],
          numericalValue: validatedValue === '' ? null : Number(validatedValue),
        }),
      );
    } else {
      const validatedValue = validateValue(highValues[index]);
      setHighValues((prev) => {
        const newValues = [...prev];
        newValues[index] = validatedValue;
        return newValues;
      });
      const problemId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(highValueData.id) + index + 1}`;
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [highValueData.id, problemId],
          numericalValue: validatedValue === '' ? null : Number(validatedValue),
        }),
      );
    }

    // Lowの全データが入力済みになったらLowの平均値計算をする
    if (isCalculateLowAverage()) {
      let sum = 0;
      const lowAverageId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(lowValueData.id) + averageIndex + 1}`;
      lowValues.forEach((value, index) => {
        if (index !== averageIndex) {
          sum += Number(value);
        }
      });
      lowAverage = roundValue(sum / averageIndex, DECIMAL_MAX_DIGITS);
      setLowValues((prev) => {
        const newValues = [...prev];
        newValues[averageIndex] = lowAverage.toString();
        return newValues;
      });
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [lowValueData.id, lowAverageId],
          numericalValue: lowAverage,
        }),
      );
    } else {
      // Low平均値部分のデータをクリア
      setLowValues((prev) => {
        const newValues = [...prev];
        newValues[averageIndex] = '';
        return newValues;
      });
      const lowAverageId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(lowValueData.id) + averageIndex + 1}`;
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [lowValueData.id, lowAverageId],
          numericalValue: null,
        }),
      );
    }

    // Highの全データが入力済みになったらHighの平均値計算をする
    if (isCalculateHighAverage()) {
      let sum = 0;
      highValues.forEach((value, index) => {
        if (index !== averageIndex) {
          sum += Number(value);
        }
      });
      highAverage = roundValue(sum / averageIndex, DECIMAL_MAX_DIGITS);
      setHighValues((prev) => {
        const newValues = [...prev];
        newValues[averageIndex] = highAverage.toString();
        return newValues;
      });
      const averageId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(highValueData.id) + averageIndex + 1}`;
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [highValueData.id, averageId],
          numericalValue: highAverage,
        }),
      );
    } else {
      // High平均値部分のデータをクリア
      setHighValues((prev) => {
        const newValues = [...prev];
        newValues[averageIndex] = '';
        return newValues;
      });
      const averageId = `${VARIABLE_HARDNESS.typeId}-${getIdIndex(highValueData.id) + averageIndex + 1}`;
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [highValueData.id, averageId],
          numericalValue: null,
        }),
      );
    }

    if (isCalculateLowAverage() && isCalculateHighAverage()) {
      // 差分を更新
      const diffValue = Math.abs(
        roundValue(highAverage - lowAverage, DECIMAL_MAX_DIGITS),
      );
      setDiffValue(diffValue.toString());
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [diffValueData.id],
          numericalValue: diffValue,
        }),
      );

      // 判定
      let newJudgement = 0;
      const { min, max } = diffValueData.criteria;
      if (min === null && max !== null) {
        // 上限のみ設定
        newJudgement = diffValue <= max ? 1 : 2;
      } else if (min !== null && max === null) {
        // 下限のみ設定
        newJudgement = diffValue >= min ? 1 : 2;
      } else if (min !== null && max !== null) {
        newJudgement = min <= diffValue && diffValue <= max ? 1 : 2;
      } else {
        // 基準値なし
        return;
      }

      updateAllItemsJudgement(newJudgement);
    } else {
      // 差分をクリア
      setDiffValue('');
      dispatch(
        updateJudgement({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [diffValueData.id],
          judgement: JUDGEMENT.EMPTY,
        }),
      );
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [diffValueData.id],
          numericalValue: null,
        }),
      );
    }
  };

  /** Lowの平均値計算するかどうか */
  const isCalculateLowAverage = () => {
    for (let i = 0; i < lowValues.length - 1; i++) {
      if (lowValues[i] === '') return false;
    }
    return true;
  };

  /** Highの平均値計算するかどうか */
  const isCalculateHighAverage = () => {
    for (let i = 0; i < highValues.length - 1; i++) {
      if (highValues[i] === '') return false;
    }
    return true;
  };

  /** 入力確定時のバリデーション */
  const validateValue = (value: string) => {
    // 先頭の0を削除、小数点スタートになる場合は0を補完
    return Number(value) !== 0 ? Number(value).toString() : value;
  };

  const handleCheckNa = () => {
    if (!isNa(diffValueData.judgement)) {
      clearValues();
    }
    const newJudgement = isNa(diffValueData.judgement) ? 0 : 3;
    updateAllItemsJudgement(newJudgement);
  };

  const updateAllItemsJudgement = (judgement: number) => {
    itemDataList.forEach((item) => {
      dispatch(
        updateJudgement({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [item.id],
          judgement,
        }),
      );
      if (item.itemData) {
        item.itemData.forEach((problem) => {
          dispatch(
            updateJudgement({
              categoryId: VARIABLE_HARDNESS.categoryId,
              typeId: VARIABLE_HARDNESS.typeId,
              path: [item.id, problem.id],
              judgement,
            }),
          );
        });
      }
    });
  };

  const clearValues = () => {
    // 表示文字列を初期化
    setLowValues((prev) => {
      return prev.map(() => '');
    });
    setHighValues((prev) => {
      return prev.map(() => '');
    });
    setDiffValue('');

    // 診断データを初期化
    itemDataList.forEach((item) => {
      dispatch(
        updateNumericalValue({
          categoryId: VARIABLE_HARDNESS.categoryId,
          typeId: VARIABLE_HARDNESS.typeId,
          path: [item.id],
          numericalValue: null,
        }),
      );
      if (item.itemData) {
        item.itemData.forEach((problem) => {
          dispatch(
            updateNumericalValue({
              categoryId: VARIABLE_HARDNESS.categoryId,
              typeId: VARIABLE_HARDNESS.typeId,
              path: [item.id, problem.id],
              numericalValue: null,
            }),
          );
        });
      }
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
    }
  };

  /** 判定基準のテキスト取得 */
  const getCriteriaText = (criteria: Criteria) => {
    const min = criteria.min === null ? '' : criteria.min.toString();
    const max = criteria.max === null ? '' : criteria.max.toString();
    return `${min} - ${max}`;
  };

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid
          size={2}
          mb={1}
          sx={{ fontSize: theme.typography.body1.fontSize, display: 'flex' }}
        >
          {isVisualInspection && (
            <Box sx={{ display: 'inline', mx: 1.5 }}>
              <VisibilityIcon color="primary" />
            </Box>
          )}
          <Typography sx={TEXT_OVERFLOW_2LINES}>{typeLabel}</Typography>
        </Grid>
        <Grid
          size={2}
          sx={style.columnLabel}
          textAlign="center"
          pr={theme.typography.caption.fontSize}
        >
          <Typography sx={TEXT_OVERFLOW_2LINES}>
            {lowValueData.label}
          </Typography>
        </Grid>
        <Grid
          size={2}
          sx={style.columnLabel}
          textAlign="center"
          pr={theme.typography.caption.fontSize}
        >
          <Typography sx={TEXT_OVERFLOW_2LINES}>
            {highValueData.label}
          </Typography>
        </Grid>
        <Grid
          size={2}
          sx={style.columnLabel}
          textAlign="center"
          pr={theme.typography.caption.fontSize}
        >
          <Typography sx={TEXT_OVERFLOW_2LINES}>
            {diffValueData.label}
          </Typography>
        </Grid>
        <Grid size={1} sx={style.columnLabel} textAlign="center">
          {'N/A'}
        </Grid>
        <Grid size={1}>{/* 判定結果 */}</Grid>
        <Grid size={2} sx={style.columnLabel} textAlign="center">
          {diffValueData.criteria.min || diffValueData.criteria.max
            ? t('LBL_CRITERIA')
            : ''}
        </Grid>
      </Grid>
      {rowLabels.map((label, i) => {
        const lineStyle = {
          height: '40px',
          alignItems: 'center',
          bgcolor: i % 2 === 0 ? theme.palette.background.paper : '',
        };
        const isLastRow = i === rowLabels.length - 1;
        return (
          <Grid container key={`${label}_${i}`} spacing={1} sx={lineStyle}>
            <Grid
              size={2}
              sx={[
                {
                  paddingLeft: '1.5rem',
                  fontSize: theme.typography.caption.fontSize,
                },
                TEXT_OVERFLOW_2LINES,
              ]}
            >
              {label}
            </Grid>
            <Grid size={2} display="flex">
              <OutlinedInput
                type="number"
                inputMode="decimal"
                size="small"
                slotProps={{
                  input: {
                    readOnly: isLastRow,
                  },
                }}
                placeholder={
                  isNa(diffValueData.judgement) || isLastRow ? '' : '0'
                }
                disabled={isNa(diffValueData.judgement)}
                sx={style.numberInput}
                value={isNa(diffValueData.judgement) ? '' : lowValues[i]}
                onChange={(e) => handleLowValueChange(e, i)}
                onBlur={() => judgeInputValue(true, i)}
                onKeyDown={handleKeyDown}
              />
              <Typography sx={style.unit}>N</Typography>
            </Grid>
            <Grid size={2} display="flex">
              <OutlinedInput
                type="number"
                inputMode="decimal"
                size="small"
                disabled={isNa(diffValueData.judgement)}
                slotProps={{
                  input: {
                    readOnly: isLastRow,
                  },
                }}
                placeholder={
                  isNa(diffValueData.judgement) || isLastRow ? '' : '0'
                }
                sx={style.numberInput}
                value={isNa(diffValueData.judgement) ? '' : highValues[i]}
                onChange={(e) => handleHighValueChange(e, i)}
                onBlur={() => judgeInputValue(false, i)}
                onKeyDown={handleKeyDown}
              />
              <Typography sx={style.unit}>N</Typography>
            </Grid>
            {isLastRow && (
              <React.Fragment>
                {/* 差分 */}
                <Grid size={2} display="flex">
                  <OutlinedInput
                    type="number"
                    inputMode="decimal"
                    size="small"
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                    disabled={isNa(diffValueData.judgement)}
                    sx={style.numberInput}
                    value={isNa(diffValueData.judgement) ? '' : diffValue}
                  />
                  <Typography sx={style.unit}>N</Typography>
                </Grid>
                <Grid size={1} sx={{ textAlign: 'center' }}>
                  <Checkbox
                    sx={{
                      '& .MuiSvgIcon-root': { fontSize: '1.5rem' },
                    }}
                    checked={isNa(diffValueData.judgement)}
                    onChange={handleCheckNa}
                  />
                </Grid>
                <Grid size={1}>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: theme.typography.caption.fontSize,
                      color: getColorByJudgement(diffValueData.judgement),
                    }}
                  >
                    {
                      JUDGEMENT_LABEL[
                        diffValueData.numericalValue !== undefined
                          ? diffValueData.judgement
                          : 0
                      ]
                    }
                  </Typography>
                </Grid>
                <Grid size={2}>
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: theme.typography.caption.fontSize,
                    }}
                  >
                    {getCriteriaText(diffValueData.criteria)}
                  </Typography>
                </Grid>
              </React.Fragment>
            )}
          </Grid>
        );
      })}
    </Box>
  );
};

export default VariableHardness;
